export default {
  install: (Vue, options) => {
    const hasConsecutiveAlphabet = (str) => {
      const value = str.toLowerCase();
      for (let i = 0; i < value.length - 2; i++) {
        const a = value.charCodeAt(i);
        if (
          a >= 97 &&
          a <= 122 &&
          value.charCodeAt(i + 1) === a + 1 &&
          value.charCodeAt(i + 2) === a + 2
        ) {
          return true;
        }
      }
      return false;
    };

    const hasConsecutiveNumbers = (str) => {
      for (let i = 0; i < str.length - 2; i++) {
        const n = str.charCodeAt(i);
        if (
          n >= 48 &&
          n <= 57 &&
          str.charCodeAt(i + 1) === n + 1 &&
          str.charCodeAt(i + 2) === n + 2
        ) {
          return true;
        }
      }
      return false;
    };

    const getStrengthLabel = (score) => {
      if (score < 40) return "Weak";
      if (score < 60) return "Fair";
      if (score < 80) return "Strong";
      return "Very Strong";
    };

    const validatePassword = (password) => {
      const errors = [];
      let score = 0;

      if (password.length < 8) {
        errors.push(options.$i18n.messages[options.$i18n.locale].message.password_length_error);
      }

      if (/\s/.test(password)) {
        errors.push(options.$i18n.messages[options.$i18n.locale].message.password_whitespace_error);
      }

      const hasUpper = /[A-Z]/.test(password);
      const hasLower = /[a-z]/.test(password);
      const hasDigit = /\d/.test(password);
      const hasSpecial = /[!@#$%^&*()_\-+=[\]{}|;:'\",.<>/?]/.test(password);

      const categoryCount = [hasUpper, hasLower, hasDigit, hasSpecial].filter(Boolean).length;
      if (categoryCount < 3) {
        errors.push(options.$i18n.messages[options.$i18n.locale].message.password_types_error);
      }

      if (hasConsecutiveAlphabet(password) || hasConsecutiveNumbers(password)) {
        errors.push(options.$i18n.messages[options.$i18n.locale].message.password_consecutive_error);
      }

      if (/(.)\1{2,}/i.test(password)) {
        errors.push(options.$i18n.messages[options.$i18n.locale].message.password_repeated_error);
      }

      const bannedWords = ["password", "admin", "welcome", "qwerty", "login"];
      if (bannedWords.some(w => password.toLowerCase().includes(w))) {
        errors.push(options.$i18n.messages[options.$i18n.locale].message.password_common_error);
      }

      score += Math.min(password.length * 2, 30);
      score += categoryCount * 7.5;
      score += Math.min(new Set(password).size * 2, 20);

      if (password.length >= 12) score += 10;
      if (password.length >= 16) score += 10;

      if (hasConsecutiveAlphabet(password)) score -= 10;
      if (hasConsecutiveNumbers(password)) score -= 10;
      if (/(.)\1{2}/.test(password)) score -= 10;

      score = Math.max(0, Math.min(100, Math.round(score)));

      return {
        isValid: errors.length === 0,
        score,
        strength: getStrengthLabel(score),
        errors
      };
    };

    const isSecurityConsecutive = (code) => {
      let asc = true;
      let desc = true;
      for (let i = 0; i < code.length - 1; i++) {
        if (code[i + 1] !== String.fromCharCode(code.charCodeAt(i) + 1)) {
          asc = false;
        }
        if (code[i + 1] !== String.fromCharCode(code.charCodeAt(i) - 1)) {
          desc = false;
        }
      }
      return asc || desc;
    };

    const isSecurityRepeatedPattern = (code) => {
      for (let len = 1; len <= code.length / 2; len++) {
        const pattern = code.slice(0, len);
        if (pattern.repeat(code.length / len) === code) {
          return true;
        }
      }
      return false;
    };

    const isSecurityPalindrome = (code) => {
      return code === code.split("").reverse().join("");
    };

    const validateSecurityCode = (code) => {
      const errors = [];

      if (!/^\d{6}$/.test(code)) {
        errors.push(options.$i18n.messages[options.$i18n.locale].message.security_code_error);
        return { isValid: false, errors };
      }

      if (new Set(code).size < 2) {
        errors.push(options.$i18n.messages[options.$i18n.locale].message.security_digit_error);
      }

      if (isSecurityConsecutive(code)) {
        errors.push(options.$i18n.messages[options.$i18n.locale].message.security_consecutive_error);
      }

      if (isSecurityRepeatedPattern(code)) {
        errors.push(options.$i18n.messages[options.$i18n.locale].message.security_repeated_error);
      }

      if (isSecurityPalindrome(code)) {
        errors.push(options.$i18n.messages[options.$i18n.locale].message.security_palindrome_error);
      }

      if (/^(\d)\1{5}$/.test(code)) {
        errors.push(options.$i18n.messages[options.$i18n.locale].message.security_weak_error);
      }

      return {
        isValid: errors.length === 0,
        errors
      };
    };
    
    const LOGOUT = "user/logout";

    Vue.prototype.$helpers = {
      login() {
        options.$router.push("/login");
      },
      logout() {
        localStorage.removeItem("popupClosed");
        options.$store
          .dispatch(LOGOUT)
          .then(() => options.$router.push("/"))
          .catch(err => console.log(err));
      },
      handleFeedback(e) {
        let result = false;
        switch (e) {
          case "invalidToken":
            result = true;
            options.$store
              .dispatch(LOGOUT)
              .then(() => options.$router.push("/expired"))
              .catch(err => console.log(err));
            break;
          case "NoUserAccessRight":
            result = true;
            options.$store
              .dispatch(LOGOUT)
              .then(() => options.$router.push("/denied"))
              .catch(err => console.log(err));
            break;
        }

        return result;
      },
      alert(title, message, variant) {
        return options.$swal(title, message, variant);
      },
      showDialog(title, message, variant) {
        options.$swal(title, message, variant);
      },
      showToast(caption, message, variant) {
        options.$swal(title, message, variant);
      },
      getNumberClass(n) {
        let result = "text-primary";
        if (n < 0) {
          result = "text-danger";
        }
        return result;
      },
      getTeamClass(n, side) {
        if (n.bet_type == "HDP" || n.bet_type == "HDPH") {
          if (n.team_g == n.home_team_id && side == 0) {
            return "text-danger";
          } else if (n.team_g == n.away_team_id && side == 1) {
            return "text-danger";
          } else {
            return "text-info";
          }
        } else {
          return "";
        }
      },
      getBetClass(n) {
        if (n.bet_type == "HDP" || n.bet_type == "HDPH") {
          if (n.bet_team_id == n.home_team_id && n.home_team_id == n.team_g) {
            return "text-danger";
          } else if (
            n.bet_team_id == n.away_team_id &&
            n.away_team_id == n.team_g
          ) {
            return "text-danger";
          } else {
            return "text-info";
          }
        }
      },
      getBetDetail(n, home, away) {
        let result = "";
        switch (n.bet_type) {
          case "HDP":
          case "HDPH":
            if(n.home_away){
              result = n.home_away == 1 ? home : away;
            } else {
              if (n.bet_team_id == n.home_team_id) {
                result = home;
              } else if (n.bet_team_id == n.away_team_id) {
                result = away;
              }
            }
            break;
          case "ML":
          case "MLH":
            result = n.home_away == 1 ? home : away;
            break;
          case "OE":
          case "OEH":
          case "OEHM":
          case "OEHMH":
          case "OEAW":
          case "OEAWH":
            result =
              n.home_away == 1
                ? options.$i18n.messages[options.$i18n.locale].ui.odd
                : options.$i18n.messages[options.$i18n.locale].ui.even;
            break;
          case "OU":
          case "OUH":
            result =
              n.home_away == 1
                ? options.$i18n.messages[options.$i18n.locale].ui.over
                : options.$i18n.messages[options.$i18n.locale].ui.under;
            break;
          case "1X2":
          case "1X2H":
          case "1X2HDP":
          case "1X2HDPH":
            if([54].includes(n.sports_type)){
              if (n.home_away == 1) {
                result = options.$i18n.messages[options.$i18n.locale].m.MBR_SINGLE;
              } else if (n.home_away == 2) {
                result = options.$i18n.messages[options.$i18n.locale].m.MBR_TRIPS;
              } else if (n.home_away == 3) {
                result = options.$i18n.messages[options.$i18n.locale].m.MBR_PAIR;
              }
            } else {
              if (n.home_away == 1) {
                result = home;
              } else if (n.home_away == 2) {
                result = away;
              } else if (n.home_away == 3) {
                result = options.$i18n.messages[options.$i18n.locale].ui.draw;
              }
            }
            break;
          case "CS":
          case "CSH":
          case "TG":
          case "TGH":
          case "OR":
          case "BS":
            result = n.criteria1;
            break;
          case "CSHTFT":
          case "ETGHTFT":
            if (n.criteria2) {
              let a = JSON.parse(n.criteria2);
              result = n.criteria1 + " / <" + a.odds_col_criteria + ">";
              // result = n.criteria1 + " / <" + a.home_score_ht + "-" + a.away_score_ht + ">";
            } else {
              result = n.criteria1 + " / " + "-";
            }
            break;
          case "DC":
          case "DCH":
            if (n.criteria1 == "HD") {
              result = options.$i18n.messages[options.$i18n.locale].m.BT_HD;
            } else if (n.criteria1 == "HA") {
              result = options.$i18n.messages[options.$i18n.locale].m.BT_HA;
            } else if (n.criteria1 == "DA") {
              result = options.$i18n.messages[options.$i18n.locale].m.BT_DA;
            } else {
              result = n.criteria1;
            }
            break;
          case "FGLG":
          case "FGLGH":
            if (n.criteria1 == "HFG") {
              result = options.$i18n.messages[options.$i18n.locale].m.BT_HFG;
            } else if (n.criteria1 == "AFG") {
              result = options.$i18n.messages[options.$i18n.locale].m.BT_AFG;
            } else if (n.criteria1 == "HLG") {
              result = options.$i18n.messages[options.$i18n.locale].m.BT_HLG;
            } else if (n.criteria1 == "ALG") {
              result = options.$i18n.messages[options.$i18n.locale].m.BT_ALG;
            } else if (n.criteria1 == "NG") {
              result = options.$i18n.messages[options.$i18n.locale].m.BT_NG;
            } else {
              result = n.criteria1;
            }
            break;
          case "HTFT":
          case "HTFTH":
            if (n.criteria1 == "HH") {
              result = options.$i18n.messages[options.$i18n.locale].m.BT_HH;
            } else if (n.criteria1 == "HD") {
              result = options.$i18n.messages[options.$i18n.locale].m.BT_HD;
            } else if (n.criteria1 == "HA") {
              result = options.$i18n.messages[options.$i18n.locale].m.BT_HA;
            } else if (n.criteria1 == "DH") {
              result = options.$i18n.messages[options.$i18n.locale].m.BT_DH;
            } else if (n.criteria1 == "DD") {
              result = options.$i18n.messages[options.$i18n.locale].m.BT_DD;
            } else if (n.criteria1 == "DA") {
              result = options.$i18n.messages[options.$i18n.locale].m.BT_DA;
            } else if (n.criteria1 == "AH") {
              result = options.$i18n.messages[options.$i18n.locale].m.BT_AH;
            } else if (n.criteria1 == "AD") {
              result = options.$i18n.messages[options.$i18n.locale].m.BT_AD;
            } else if (n.criteria1 == "AA") {
              result = options.$i18n.messages[options.$i18n.locale].m.BT_AA;
            } else {
              result = n.criteria1;
            }
            break;
          case "CL":
          case "HNB":
          case "ANB":
          case "DNB":
          case "DNBH":
          case "TWTN":
          case "ETG":
          case "ETGH":
          case "EHTG":
          case "EHTGH":
          case "EATG":
          case "EATGH":
          case "WM":
          case "HTFTOE":
            result = n.criteria1;
            break;
          case "1X2OU":
          case "DCOU":
          case "OUOE":
            result = n.criteria1 + "(" + n.criteria2 + ")";
            break;
        }

        return result;
      },
      getDisplayBall(n) {
        // if (n.bet_type == "HDP" || n.bet_type == "HDPH") {
        //   if (n.bet_team_id == n.home_team_id && n.home_team_id == n.team_g) {
        //     return -n.ball;
        //   } else if (
        //     n.bet_team_id == n.away_team_id &&
        //     n.away_team_id == n.team_g
        //   ) {
        //     return -n.ball;
        //   } else {
        //     return n.ball;
        //   }
        // } else {
        //   return n.ball;
        // }
        let result = "";
        if (n.bet_type == "HDP" || n.bet_type == "HDPH") {
          if (n.bet_team_id == n.home_team_id && n.home_team_id == n.team_g) {
            result = -n.ball;
          } else if (n.bet_team_id == n.away_team_id && n.away_team_id == n.team_g) {
            result = -n.ball;
          } else {
            result = n.ball;
          }
        } else {
          result = n.ball_display;
        }

        if(n.bet_type_share == "HDPMMO"){
          // result = result + " (" + n.criteria2 + ")";
          if (n.bet_team_id == n.home_team_id && n.home_team_id == n.team_g) {
            result = -n.ball_display + " (" + n.criteria2 + ")";
          } else if (n.bet_team_id == n.away_team_id && n.away_team_id == n.team_g) {
            result = -n.ball_display + " (" + n.criteria2 + ")";
          } else {
            result = n.ball_display + " (" + n.criteria2 + ")";
          }
        }

        return result;
      },
      displayBetDetail: function(item) {
        let accType = options.$store.getters.accountType;
        switch (accType) {
          case 2:
            return item.agent_pt / 100 + " x " + item.bet_member;
          case 3:
            return item.master_pt / 100 + " x " + item.bet_member;
          case 4:
            return item.senior_pt / 100 + " x " + item.bet_member;
          case 5:
            return item.s_senior_pt / 100 + " x " + item.bet_member;
          case 6:
            return item.shareholder_pt / 100 + " x " + item.bet_member;
        }
      },
      generatePositionShare: function(downlineLevel, item) {
        var result = "";
        if(downlineLevel == 6) result = result.concat(this.getUplineName(6) + " " + item.shareholder_pt + "%; ");
        if(downlineLevel >= 5) result = result.concat(this.getUplineName(5) + " " + item.s_senior_pt + "%; ");
        if(downlineLevel >= 4) result = result.concat(this.getUplineName(4) + " " + item.senior_pt + "%; ");
        if(downlineLevel >= 3) result = result.concat(this.getUplineName(3) + " " + item.master_pt + "%; ");
        if(downlineLevel >= 2) result = result.concat(this.getUplineName(2) + " " + item.agent_pt + "%; ");
        return result;
      },
      getCreditLimit: function(account_id, account_type) {
        options.$store
          .dispatch("user/getCreditUsedDownline", { account_id, account_type })
          .then()
          .catch(err => {});
      },
      getSchedule: function() {
        options.$store
          .dispatch("user/getSchedule")
          .then()
          .catch(err => {});
      },
      getWallet: function() {
        options.$store
          .dispatch("user/getWallet")
          .then()
          .catch(err => {});
      },
      getPlatform: function() {
        let account_id = options.$store.getters.accountID;
        options.$store
          .dispatch("user/getPlatform", { account_id })
          .then()
          .catch(err => {});
      },
      checkPartner: function() {
        if (options.$store.getters.accountType != 6) {
          // Clear stale cookie/persisted flag for non-type-6 logins
          options.$store.commit("user/updateIsPartner", false);
          return;
        }
        options.$store
          .dispatch("user/checkPartner")
          .then()
          .catch(err => {});
      },
      getPositionTaking: function() {
        let account_id = options.$store.getters.accountID;
        options.$store
          .dispatch("user/getPositionTaking", { account_id })
          .then()
          .catch(err => {});
      },
      getCommission: function() {
        let account_id = options.$store.getters.accountID;
        options.$store
          .dispatch("user/getCommission", { account_id })
          .then()
          .catch(err => {});
      },
      getBetLimit: function() {
        let account_id = options.$store.getters.accountID;
        options.$store
          .dispatch("user/getBetLimit", { account_id })
          .then()
          .catch(err => {});
      },
      getCommRateList: function(range) {
        let cmRange = [];
        if (range) {
          let increment = 0.0;
          let div = Math.ceil(range / 0.05);
          for (let i = 0; i <= div; i++) {
            let val = parseFloat(increment.toFixed(2));
            increment = increment + 0.05;
            cmRange.push({ value: val, text: val + "%" });
          }
        } else {
          cmRange.push({ value: 0, text: "0%" });
        }
        return cmRange;
      },
      getCommRateList1: function(parentRange, childRange) {
        let cmRange = [];
        let range = parentRange;
        if (childRange > parentRange) {
          range = childRange;
        }
        if (range) {
          let increment = 0.0;
          let div = Math.ceil(range / 0.05);
          for (let i = 0; i <= div; i++) {
            let val = parseFloat(increment.toFixed(2));
            increment = increment + 0.05;
            cmRange.push({ value: val, text: val + "%" });
          }
        } else {
          cmRange.push({ value: 0, text: "0%" });
        }
        return cmRange;
      },
      getPTRateList: function(range) {
        let ptRange = [];
        if (range) {
          let increment = 0.0;
          let div = Math.ceil(range / 0.5);
          for (let i = 0; i <= div; i++) {
            let val = parseFloat(increment.toFixed(2));
            increment = increment + 0.5;
            ptRange.push({ value: val, text: val + "%" });
          }
        } else {
          ptRange.push({ value: 0, text: "0%" });
        }
        return ptRange;
      },
      getUplineName: function(account_type) {
        let account_name = "";
        switch (account_type) {
          case 6:
            account_name =
              options.$i18n.messages[options.$i18n.locale].ui.share_holder;
            break;
          case 5:
            account_name =
              options.$i18n.messages[options.$i18n.locale].ui.super_senior;
            break;
          case 4:
            account_name =
              options.$i18n.messages[options.$i18n.locale].ui.senior;
            break;
          case 3:
            account_name =
              options.$i18n.messages[options.$i18n.locale].ui.master;
            break;
          case 2:
            account_name =
              options.$i18n.messages[options.$i18n.locale].ui.agent;
            break;
          case 1:
            account_name =
              options.$i18n.messages[options.$i18n.locale].ui.member;
            break;
        }

        return account_name;
      },
      getLabelName: function(accType){
        switch(accType){
          case 6:
            return "SH";
          case 5:
            return "SS";
          case 4:
            return "SN";
          case 3:
            return "MA";
          case 2:
            return "AG";
        }
      },
      isPlatformEnabled: function(x) {
        let list = options.$store.getters.getPlatformList;
        let code = "";
        let z = [];
        if(list) {
          list.forEach(y => {
            z.push(y.platform_code);
          });
          switch(x){
            case "LC":
              code = "LIVECASINO";
              break;
            case "SL":
              code = "SLOTS";
              break;
            case "LO":
              code = "LOTTO";
              break;
            case "KN":
              code = "KENO";
              break;
            case "CF":
              code = "COCKFIGHT";
              break;
            case "LT":
              code = "LOTTERY";
              break;
          }
          return z.includes(code);
        }
      },
      numberFormat: function(value) {
        let result = "";
        if (value) {
          let rawValue = parseInt(value.toString().replace(/,/g, ""));
          result = rawValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        } else {
          result = "0";
        }
        return result;
      },
      settlementFormat: function(value, decimal) {
        let result = "";
        if (value) {
          let rawValue = value.toString().replace(/,/g, "");
          let part1 = parseFloat(rawValue)
            .toFixed(decimal)
            .split(".")[0];
          let part2 = parseFloat(rawValue)
            .toFixed(decimal)
            .split(".")[1];
          result =
            part1.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") +
            "." +
            part2;
        } else {
          result = "0";
        }
        return result;
      },
      convertSettlementNumber: function(value) {
        return value ? parseFloat(value.toString().replace(/,/g, "")).toFixed(2) : value;
      },
      isDayEnabledList: function() {
        let result = [];
        let list = options.$store.getters.getScheduleList;
        if (list) {
          for (let count = 1; count < 8; count++) {
            let isEnabled = list.Table[0]["schedule_" + count];
            if (isEnabled == true) {
              result.push({
                value: "schedule_" + count,
                text: options.$i18n.messages[options.$i18n.locale].ui[
                  "schedule_" + count
                ].split("|")[0],
                disabled: count == 1 ? true : false
              });
            }
          }
        }
        return result;
      },
      isDayEnabled: function() {
        let result = [];
        let list = options.$store.getters.getScheduleList;
        if (list) {
          for (let count = 1; count < 8; count++) {
            let isEnabled = list.Table[0]["schedule_" + count];
            if (isEnabled == true) {
              result.push("schedule_" + count);
            }
          }
        }
        return result;
      },
      convertNumber: function(value) {
        return parseInt(value.toString().replace(/,/g, ""));
      },
      copyAllUpline_title: function() {
        return options.$i18n.messages[options.$i18n.locale].message
          .copy_all_upline;
      },
      copyAllAccount_title: function() {
        return options.$i18n.messages[options.$i18n.locale].message
          .copy_all_account;
      },
      copyAllRemaining_title: function() {
        return options.$i18n.messages[options.$i18n.locale].message
          .copy_all_remaining;
      },
      copyAllMin_title: function() {
        return options.$i18n.messages[options.$i18n.locale].message
          .copy_all_min;
      },
      copyAllMinBet_title: function() {
        return options.$i18n.messages[options.$i18n.locale].message
          .copy_all_min_bet;
      },
      copyAllMaxBet_title: function() {
        return options.$i18n.messages[options.$i18n.locale].message
          .copy_all_max_bet;
      },
      copyAllMaxPerMatch_title: function() {
        return options.$i18n.messages[options.$i18n.locale].message
          .copy_all_max_per_match;
      },
      isAgentLevel: function() {
        return options.$store.getters.accountType == 2;
      },
      isMemberLevel: function(account_type) {
        return account_type == 1;
      },
      selectText: function(element) {
        let doc = document;
        let text = doc.getElementById(element);
        let range, selection;

        if (doc.body.createTextRange) {
          range = document.body.createTextRange();
          range.moveToElementText(text);
          range.select();
        } else if (window.getSelection) {
          selection = window.getSelection();
          range = document.createRange();
          range.selectNodeContents(text);
          selection.removeAllRanges();
          selection.addRange(range);
        }
      },
      getVisible: function(e) {
        let accountRole = options.$store.getters.authorityRole;
        if (accountRole == "SUB_PRI") {
          if (e.length > 0) {
            let result = false;
            let pages = e;
            let roles = options.$store.getters.accessRoles.authority_option;
            roles = roles.split(",");
            for (var i = 0; i < pages.length; i++) {
              for (var j = 0; j < roles.length; j++) {
                if (roles[j] == pages[i]) {
                  result = true;
                  break;
                }
              }
            }
            return result;
          } else {
            return true;
          }
        } else {
          return true;
        }
      },
      isSuspended: function() {
        let result = true;
        let accStatus = options.$store.getters.accountStatus;
        if (accStatus) {
          result = accStatus != "ACTIVE" ? true : false;
        }
        return result;
      },
      getPTSB: function(response, betType, sportType, type) {
        let result = "";
        let item = response;
        item.forEach(value => {
          if (value.bet_type == betType && value.sport_type == sportType) {
            result = value[type];
          }
        });
        return result;
      },
      getOddsType(n) {
        var result = "";
        switch(n.odds_type) {
          case 1:
            if((n.criteria2 && ["HDP","HDPH","OU","OUH"].includes(n.bet_type.toUpperCase()) && !n.cash_out) || n.bet_type_share == "PARLAYMMO") {
              result = "(MMO)";
            } else {
              result = "(MY)";
            }
            break;
          case 2:
            result = "(HK)";
            break;
          case 3:
            result = "(ID)";
            break;
          case 4:
            result = "(DEC)";
            break;
        }
        return result;
      },
      getBetType: function() {
        let list = [
          { value: 'ALL', text: options.$i18n.messages[options.$i18n.locale].ui.all },
          { value: 'HDP', text: options.$i18n.messages[options.$i18n.locale].m.BT_HDP.split("|")[0] },
          { value: 'OU', text: options.$i18n.messages[options.$i18n.locale].m.BT_OU.split("|")[0] },
          { value: '1X2', text: options.$i18n.messages[options.$i18n.locale].m.BT_1X2 },
          { value: 'OE', text: options.$i18n.messages[options.$i18n.locale].m.BT_OE.split("|")[0] },
          { value: 'CS', text: options.$i18n.messages[options.$i18n.locale].m.BT_CS },
          { value: 'TG', text: options.$i18n.messages[options.$i18n.locale].m.BT_TG },
          { value: 'DC', text: options.$i18n.messages[options.$i18n.locale].m.BT_DC },
          { value: 'OR', text: options.$i18n.messages[options.$i18n.locale].m.BT_OR },
          { value: 'ML', text: options.$i18n.messages[options.$i18n.locale].m.BT_ML },
          { value: 'CL', text: options.$i18n.messages[options.$i18n.locale].m.BT_CL },
          { value: '1X2HDP', text: options.$i18n.messages[options.$i18n.locale].m.BT_1X2HDP },
          { value: 'BS', text: options.$i18n.messages[options.$i18n.locale].m.BT_BS },
          { value: 'HNB', text: options.$i18n.messages[options.$i18n.locale].m.BT_HNB },
          { value: 'DNB', text: options.$i18n.messages[options.$i18n.locale].m.BT_DNB },
          { value: 'ANB', text: options.$i18n.messages[options.$i18n.locale].m.BT_ANB },
          { value: 'PARLAY', text: options.$i18n.messages[options.$i18n.locale].m.BT_PARLAY },
          { value: 'HTFT', text: options.$i18n.messages[options.$i18n.locale].m.BT_HTFT },
          { value: 'FGLG', text: options.$i18n.messages[options.$i18n.locale].m.BT_FGLG },
          { value: 'TWTN', text: options.$i18n.messages[options.$i18n.locale].m.BT_TWTN }
        ];

        return list;
      },
      showWinStatus: function(item) {
        let payout = item.payout_odds;
        let display = item.odds_display;
        let status = item.status;
        let detail_status = item.detail_status;
        let result = "";
        switch (true) {
          case payout === 0:
            result = options.$i18n.messages[options.$i18n.locale].ui.lose;
            break;
          case payout > 0 && payout < 1:
            result = options.$i18n.messages[options.$i18n.locale].ui.lose_half;
            break;
          case payout === 1:
            if(detail_status == 2) {
              result = options.$i18n.messages[options.$i18n.locale].ui.void;
            } else {
              if(status == 5) {
                result = options.$i18n.messages[options.$i18n.locale].ui.abandoned;
              } else if(status == 2) {
                result = options.$i18n.messages[options.$i18n.locale].ui.cancel;
              } else {
                result = options.$i18n.messages[options.$i18n.locale].ui.draw_cancel;
              }
            }
            break;
          case payout < display && payout > 1:
            result = options.$i18n.messages[options.$i18n.locale].ui.win_half;
            break;
          case payout > 1:
            result = options.$i18n.messages[options.$i18n.locale].ui.won;
            break;
          default:
            result = options.$i18n.messages[options.$i18n.locale].ui.running;
            break;
        }

        return result.toUpperCase();
      },
      showWinStatusMMO: function(item) {
        let payout = item.payout_odds;
        let display = item.odds_display;
        let status = item.status;
        let detail_status = item.detail_status;
        let result = "";
        switch (true) {
          case payout === 0:
            result = options.$i18n.messages[options.$i18n.locale].ui.lose;
            break;
          case payout > 0 && payout < 1:
            result = options.$i18n.messages[options.$i18n.locale].ui.lose_half;
            break;
          case payout === 1:
            if(detail_status == 2) {
              result = options.$i18n.messages[options.$i18n.locale].ui.void;
            } else {
              if(status == 5) {
                result = options.$i18n.messages[options.$i18n.locale].ui.abandoned;
              } else if(status == 2) {
                result = options.$i18n.messages[options.$i18n.locale].ui.cancel;
              } else {
                result = options.$i18n.messages[options.$i18n.locale].ui.draw_cancel;
              }
            }
            break;
          case payout > 1 && payout < 2:
            result = options.$i18n.messages[options.$i18n.locale].ui.win_half;
            break;
          case payout === 2:
            result = options.$i18n.messages[options.$i18n.locale].ui.won;
            break;
          default:
            result = options.$i18n.messages[options.$i18n.locale].ui.running;
            break;
        }

        return result.toUpperCase();
      },
      getDownlineDetails(level, i) {
        var result = []
        if(level >= 2) result = result.concat([
          i.agent_winlose.toFixed(2),
          i.agent_comm.toFixed(2),
          i.agent_winlose_comm.toFixed(2)
        ])

        if(level >= 3) result = result.concat([
          i.master_winlose.toFixed(2),
          i.master_comm.toFixed(2),
          i.master_winlose_comm.toFixed(2)
        ])

        if(level >= 4) result = result.concat([
          i.senior_winlose.toFixed(2),
          i.senior_comm.toFixed(2),
          i.senior_winlose_comm.toFixed(2)
        ])

        if(level >= 5) result = result.concat([
          i.s_senior_winlose.toFixed(2),
          i.s_senior_comm.toFixed(2),
          i.s_senior_winlose_comm.toFixed(2)
        ])
        
        if(level == 6) result = result.concat([
          i.shareholder_winlose.toFixed(2),
          i.shareholder_comm.toFixed(2),
          i.shareholder_winlose_comm.toFixed(2)
        ])
        
        return result
      },
      getStatusList: function() {
        return [
          { value: "", text: options.$i18n.messages[options.$i18n.locale].ui.all },
          { value: "active", text: options.$i18n.messages[options.$i18n.locale].ui.active },
          { value: "close", text: options.$i18n.messages[options.$i18n.locale].ui.close },
          // { value: "freeze", text: options.$i18n.messages[options.$i18n.locale].ui.freeze },
          { value: "suspend", text: options.$i18n.messages[options.$i18n.locale].ui.suspend }
        ];
      },
      passwordFormat(value){
        const containsUppercase = /[A-Z]/.test(value);
        const containsLowercase = /[a-z]/.test(value);
        const containsNumber = /[0-9]/.test(value);
        //const containsSpecial = /[#?!@$%^&*-]/.test(value)
        return containsUppercase && containsLowercase && containsNumber;
      },
      validatePassword(password) {
        return validatePassword(password);
      },
      validateSecurityCode(code) {
        return validateSecurityCode(code);
      },
      getErrorMessage(errorTag, fallback = "system_error") {
        let tag = errorTag;
        if (tag && typeof tag === "object") {
          tag = tag.body && tag.body.errorTag ? tag.body.errorTag : null;
        }
        if (typeof tag !== "string" || !tag) {
          tag = fallback;
        }
        const key = `message.${tag}`;
        if (options.$i18n.te(key)) {
          return options.$i18n.t(key);
        }
        const fallbackKey = `message.${fallback}`;
        if (options.$i18n.te(fallbackKey)) {
          return options.$i18n.t(fallbackKey);
        }
        return options.$i18n.t("message.system_error");
      },
    };
  }
};
